import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, CircularProgress, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  Circle as CircleIcon,
  Square as SquareIcon,
  ChangeHistory as ChangeHistoryIcon,
  Diamond as DiamondIcon,
  Rectangle as RectangleIcon,
  Star as StarIcon,
  Hexagon as HexagonIcon,
  PanoramaFishEye as PanoramaFishEyeIcon,
  Add as AddIcon,
  Pentagon as PentagonIcon
} from '@mui/icons-material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useParams } from 'react-router-dom';
import { GetStoreOverviewLayout } from '../../../api/index';

const fixtureIconMap = {
  'Wall Bay': <CircleIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Impulse Fixture': <SquareIcon sx={{ fontSize: 16, color: 'white' }} />,
  'A Rail': <ChangeHistoryIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Long Table': <DiamondIcon sx={{ fontSize: 16, color: 'white' }} />,
  'H Gandola': <RectangleIcon sx={{ fontSize: 16, color: 'white' }} />,
  '4 Way': <HexagonIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Nested Table': <StarIcon sx={{ fontSize: 16, color: 'white' }} />,
  'U Bar': <PanoramaFishEyeIcon sx={{ fontSize: 16, color: 'white' }} />,
  Gondola: <PentagonIcon sx={{ fontSize: 16, color: 'white' }} />,
  mannequine: <AddIcon sx={{ fontSize: 16, color: 'white' }} />
};

const StoreLayoutView = ({ onBaySelect }) => {
  const params = useParams();
  const store_id = params.store || params.storeId || params.store_id || localStorage.getItem('analysisStoreId')?.replace(/"/g, '');
  const [loading, setLoading] = useState(true);
  const [layoutData, setLayoutData] = useState(null);
  const [bays, setBays] = useState([]);
  const imageRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const selectedDate = useSelector((state) => state.customization.selectedDate)
    .toISOString()
    .slice(0, 10);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const layoutRes = await GetStoreOverviewLayout({ dates: selectedDate, store_id });
        if (layoutRes?.data) {
          setLayoutData(layoutRes.data);
          const allBays = layoutRes.data.data[0]?.store_config?.flatMap((category) => category.bays) || [];
          setBays(allBays);
        }
      } catch (error) {
        console.error('Error fetching store layout:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [store_id, selectedDate]);

  // const findDimensions = (event) => {
  //   const { naturalWidth } = event.target;
  //   const imgDiv = imageRef.current;
  //   if (imgDiv) {
  //     const { width } = imgDiv.getBoundingClientRect();
  //     const _scaleFactor = width / naturalWidth;
  //   }
  // };

  const handleBayClick = (bay) => {
    if (onBaySelect) {
      onBaySelect(bay);
    }
  };

  const getBayColor = (bay) => {
    if (!bay.hasCapture) return '#9e9e9e';
    const details = bay.captureDetails ?? [];
    const totalFullness = details.reduce((sum, item) => sum + (item.fullness ?? 0), 0);
    const avgFullness = details.length > 0 ? totalFullness / details.length : 0;
    if (avgFullness > 90) return '#4caf50';
    if (avgFullness >= 50) return '#ffb300';
    return '#e53935';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'auto',
          minHeight: 400,
          maxHeight: 600,
          border: '1px solid #ccc',
          background: '#fafafa',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <TransformWrapper>
          <TransformComponent>
            <div
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: 800,
                aspectRatio: '4/3',
                background: '#eee'
              }}
            >
              <img
                ref={imageRef}
                src={layoutData?.data[0]?.layout_image}
                alt="Store Layout"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
                // onLoad={findDimensions}
              />
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                {bays.map((bay) => {
                  const topPercent = bay.coordinates?.y || 0;
                  const leftPercent = bay.coordinates?.x || 0;
                  const icon = fixtureIconMap[bay.fixture_name] || <SquareIcon sx={{ fontSize: 16, color: 'white' }} />;
                  return (
                    <Tooltip
                      key={bay._id}
                      title={
                        <div style={{ padding: '4px 8px' }}>
                          <div>
                            <strong>Bay ID:</strong> {bay.bay_id}
                          </div>

                          <div>
                            <strong>Fixture:</strong> {bay.fixture_name}
                          </div>
                          <div>
                            <strong>Type:</strong> {bay.type}
                          </div>
                        </div>
                      }
                      arrow
                      placement="bottom"
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleBayClick(bay)}
                        style={{
                          backgroundColor: getBayColor(bay),
                          minWidth: isMobile ? '20px' : '30px',
                          width: isMobile ? '20px' : '30px',
                          height: isMobile ? '20px' : '30px',
                          borderRadius: '50%',
                          padding: 0,
                          border: '2px solid white',
                          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'absolute',
                          top: `${topPercent}%`,
                          left: `${leftPercent}%`,
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2
                        }}
                      >
                        {icon}
                      </Button>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </TransformComponent>
        </TransformWrapper>
      </Box>
    </Box>
  );
};

export default StoreLayoutView;
